import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "../i18n/LanguageContext";
import { usePortal } from "../context/PortalContext";
import { fetchStoreSettings } from "../commerce";

const SOCIAL_DEFAULTS = {
  social_instagram: "https://www.instagram.com/cafexir_/",
  social_snapchat: "https://www.snapchat.com/add/cafexir0",
  social_tiktok: "https://www.tiktok.com/@cafexir_/",
};

export default function Footer() {
  const { t } = useLanguage();
  const { go } = usePortal();
  const { data: settings } = useQuery({ queryKey: ["store-settings"], queryFn: fetchStoreSettings, staleTime: 60000 });
  const socials = { ...SOCIAL_DEFAULTS, ...(settings || {}) };

  const goContact = () => {
    const scroll = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    if (window.location.pathname === "/") {
      scroll();
    } else {
      go("/");
      setTimeout(scroll, 1800);
    }
  };

  const goHomeSection = (id) => () => {
    const scroll = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    if (window.location.pathname === "/") {
      scroll();
    } else {
      go("/");
      setTimeout(scroll, 1800);
    }
  };

  return (
    <footer className="border-t hairline bg-xir-dark text-xir-paper" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 sm:px-10 md:grid-cols-3 md:py-24">
        <div>
          <p className="font-display text-3xl font-semibold tracking-[0.06em]">CAFÉXIR</p>
          <p className="mt-3 text-sm text-xir-paper/60">{t("footer.tagline")}</p>
        </div>
        <nav className="flex flex-col gap-3">
          <button onClick={() => go("/shop")} data-testid="footer-shop-link" className="w-fit text-start text-sm text-xir-paper/60 transition-colors hover:text-xir-paper">
            {t("nav.shop")}
          </button>
          <button onClick={() => go("/story")} data-testid="footer-story-link" className="w-fit text-start text-sm text-xir-paper/60 transition-colors hover:text-xir-paper">
            {t("nav.story")}
          </button>
          <button onClick={goContact} data-testid="footer-contact-link" className="w-fit text-start text-sm text-xir-paper/60 transition-colors hover:text-xir-paper">
            {t("footer.contact")}
          </button>
          <button onClick={goHomeSection("how-to-order")} data-testid="footer-howto-link" className="w-fit text-start text-sm text-xir-paper/60 transition-colors hover:text-xir-paper">
            {t("footer.howto")}
          </button>
          <button onClick={goHomeSection("faq")} data-testid="footer-faq-link" className="w-fit text-start text-sm text-xir-paper/60 transition-colors hover:text-xir-paper">
            {t("footer.faq")}
          </button>
          <button onClick={() => go("/policies")} data-testid="footer-policies-link" className="w-fit text-start text-sm text-xir-paper/60 transition-colors hover:text-xir-paper">
            {t("footer.policies")}
          </button>
          <p className="mt-3 text-[0.65rem] tracking-[0.15em] text-xir-gold/60" data-testid="footer-trust">
            {t("footer.codLine")}
          </p>
        </nav>
        <div className="md:text-end">
          <p className="mb-4 text-[0.65rem] uppercase tracking-[0.3em] text-xir-gold/70">{t("social.follow")}</p>
          <div className="flex gap-7 md:justify-end" data-testid="social-links">
            <a
              href={socials.social_instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-instagram"
              className="text-sm text-xir-paper/60 transition-colors hover:text-xir-gold"
            >
              Instagram
            </a>
            <a
              href={socials.social_snapchat}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-snapchat"
              className="text-sm text-xir-paper/60 transition-colors hover:text-xir-gold"
            >
              Snapchat
            </a>
            <a
              href={socials.social_tiktok}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-tiktok"
              className="text-sm text-xir-paper/60 transition-colors hover:text-xir-gold"
            >
              TikTok
            </a>
          </div>
          <p className="mt-6 text-sm leading-relaxed text-xir-paper/40">{t("footer.line")}</p>
        </div>
      </div>
      <div className="border-t hairline">
        <p className="mx-auto max-w-7xl px-6 py-6 text-[0.65rem] tracking-[0.2em] text-xir-paper/40 sm:px-10" dir="ltr">
          {t("footer.rights")}
        </p>
      </div>
    </footer>
  );
}
